'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, Compass, Gamepad2, BookOpen, Swords, Trophy,
  Users, Briefcase, BarChart3, Wallet, Gift, UserPlus, MessageCircle,
  Bell, User, Settings, HelpCircle, ChevronLeft, ChevronRight, Flame, Star,
  ShieldAlert, DollarSign, FileText, Activity, Shield, Sword
} from 'lucide-react';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activePath: string;
}

const navGroups = [
  {
    label: 'Main',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', href: '/user-dashboard' },
      { icon: Compass, label: 'Discover', href: '/discover-screen' },
      { icon: Flame, label: 'Activity', href: '/activity-detail-page' },
    ],
  },
  {
    label: 'Play',
    items: [
      { icon: Gamepad2, label: 'Games', href: '/games' },
      { icon: BookOpen, label: 'Quizzes', href: '/quiz-arena' },
      { icon: Sword, label: '1v1 Match', href: '/match', badge: 'Live' },
      { icon: Swords, label: 'Challenges', href: '/discover-screen' },
      { icon: Trophy, label: 'Tournaments', href: '/discover-screen' },
      { icon: Users, label: 'Teams', href: '/discover-screen' },
    ],
  },
  {
    label: 'Compete',
    items: [
      { icon: Briefcase, label: 'Professions', href: '/discover-screen' },
      { icon: BarChart3, label: 'Leaderboard', href: '/user-dashboard' },
    ],
  },
  {
    label: 'Earn',
    items: [
      { icon: Wallet, label: 'Wallet', href: '/wallet-screen', badge: '$124.50' },
      { icon: Gift, label: 'Rewards', href: '/user-dashboard' },
      { icon: UserPlus, label: 'Refer Friends', href: '/user-dashboard' },
    ],
  },
  {
    label: 'Account',
    items: [
      { icon: MessageCircle, label: 'Messages', href: '/user-dashboard', badge: '3' },
      { icon: Bell, label: 'Notifications', href: '/user-dashboard', badge: '7' },
      { icon: User, label: 'Profile', href: '/user-dashboard' },
      { icon: Settings, label: 'Settings', href: '/user-dashboard' },
      { icon: HelpCircle, label: 'Help', href: '/user-dashboard' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { icon: LayoutDashboard, label: 'Admin Panel', href: '/admin' },
      { icon: Users, label: 'Users', href: '/admin/users' },
      { icon: Activity, label: 'Activities', href: '/admin/activities' },
      { icon: DollarSign, label: 'Finance', href: '/admin/finance' },
      { icon: ShieldAlert, label: 'Fraud Alerts', href: '/admin/fraud', badge: '23' },
      { icon: Shield, label: 'KYC Queue', href: '/admin/kyc', badge: '156' },
      { icon: FileText, label: 'Audit Logs', href: '/admin/audit-logs' },
    ],
  },
];

export default function Sidebar({ collapsed, onToggle, activePath }: SidebarProps) {
  return (
    <aside
      className="relative flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden"
      style={{
        width: collapsed ? '64px' : '240px',
        background: '#0D1424',
        borderRight: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-3 min-w-0">
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-bold text-base text-foreground truncate">
              Brain Basket
            </span>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-3">
            {!collapsed && (
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const isActive = activePath === item.href;
              return (
                <Link
                  key={`nav-${item.label}`}
                  href={item.href}
                  className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon size={18} style={{ flexShrink: 0 }} />
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{
                            background: 'rgba(124, 58, 237, 0.2)',
                            color: '#A78BFA',
                            fontSize: '11px',
                          }}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User profile bottom */}
      {!collapsed && (
        <div className="p-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--muted)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
              AK
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">Arjun Kumar</p>
              <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>Level 14 · Gold</p>
            </div>
            <Star size={14} style={{ color: '#F59E0B', flexShrink: 0 }} />
          </div>
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute top-1/2 -right-3 w-6 h-6 rounded-full flex items-center justify-center z-10 transition-all duration-150 hover:scale-110"
        style={{ background: '#7C3AED', border: '2px solid #0D1424', transform: 'translateY(-50%)' }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed
          ? <ChevronRight size={12} color="#fff" />
          : <ChevronLeft size={12} color="#fff" />
        }
      </button>
    </aside>
  );
}